// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Drives battery sampling from a round's lifecycle. Android only — every path below
// short-circuits elsewhere via batteryTelemetry.isSupported().

import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';

import {
  isSupported,
  startBatteryTelemetry,
  stopBatteryTelemetry,
} from '@/services/batteryTelemetry';
import {
  attachBatteryTelemetryAcks,
  flushBatterySegments,
  type BatteryTelemetryClient,
} from '@/services/batteryTelemetryTransport';
import { getClientEnvironmentDetails } from '@/services/clientEnvironment';
import { isCourseTraceRunning } from '@/services/courseTrace';
import { isShotTrackingRunning } from '@/services/shotTracking';

export type UseBatteryTelemetryParams = {
  socketClient: BatteryTelemetryClient | null;
  roundId: string | null;
  holeNumber: number | null;
  /** Sampling runs only while the round is in play. */
  isRoundActive: boolean;
};

/** How often queued segments are offered to the socket. Sampling itself is once a minute. */
const FLUSH_INTERVAL_MS = 60_000;

export const useBatteryTelemetry = ({
  socketClient,
  roundId,
  holeNumber,
  isRoundActive,
}: UseBatteryTelemetryParams): void => {
  // Held in refs so the start/stop effect does not re-run on every hole change or socket
  // reconnect, which would chop the series into fragments for no reason.
  const holeRef = useRef(holeNumber);
  holeRef.current = holeNumber;
  const socketRef = useRef(socketClient);
  socketRef.current = socketClient;

  useEffect(() => {
    if (!isSupported() || !roundId || !isRoundActive) return undefined;

    let cancelled = false;

    const begin = async () => {
      // getClientEnvironmentDetails is a loose Record because it also carries browser fields;
      // only these two are meaningful on the platform this runs on.
      const environment = getClientEnvironmentDetails();
      const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);

      const result = await startBatteryTelemetry({
        roundId,
        appVersion: asText(environment.appVersion),
        osVersion: asText(environment.osVersion),
        context: () => ({
          appState: AppState.currentState ?? 'unknown',
          wsConnected: socketRef.current !== null,
          // The two continuous-sensor consumers. Recording them per sample is what turns a
          // device-level number into something attributable: minutes with GPS can be compared
          // against minutes without, on the same device in the same round.
          gpsActive: isCourseTraceRunning(),
          sensorsActive: isShotTrackingRunning(),
          holeNumber: holeRef.current,
        }),
      });
      if (cancelled || result !== 'started') return;

      const client = socketRef.current;
      if (client) {
        void flushBatterySegments(client);
      }
    };

    void begin();

    return () => {
      cancelled = true;
      // Sealing on the way out is what stops a round shorter than an hour producing nothing.
      void stopBatteryTelemetry().then(() => {
        const client = socketRef.current;
        return client ? flushBatterySegments(client) : undefined;
      });
    };
  }, [isRoundActive, roundId]);

  // Acks and flushing follow the socket rather than the round: a reconnect mid-round must
  // re-attach the listener, or a stored segment is never released from the queue.
  useEffect(() => {
    if (!isSupported() || !socketClient) return undefined;

    const detach = attachBatteryTelemetryAcks(socketClient);
    void flushBatterySegments(socketClient);
    const timer = setInterval(() => {
      void flushBatterySegments(socketClient);
    }, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      detach();
    };
  }, [socketClient]);
};
